import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowDownToLine, ArrowUpFromLine, Trash2, ChevronDown, ChevronUp, PenLine, Printer, Paperclip, FileText, X, Upload, Image as ImageIcon } from "lucide-react";
import { API, authHeaders } from "./shared";

const ACCEPTED = "application/pdf,image/jpeg,image/jpg,image/png,image/webp";

const itDate = (d) => (d && d.length >= 10 ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : "-");

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || "").split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function DdtModal({ movement, onClose, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const files = movement.ddt_files || [];

  const upload = async (fList) => {
    if (!fList?.length) return;
    setUploading(true);
    try {
      const payload = [];
      for (const f of Array.from(fList)) {
        if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name}: max 8 MB`); continue; }
        payload.push({ filename: f.name, content_type: f.type || "application/pdf", data_base64: await fileToBase64(f) });
      }
      if (!payload.length) return;
      const r = await axios.post(`${API}/admin/warehouse/movements/${movement.id}/ddt`, { files: payload }, authHeaders());
      toast.success(`${r.data.files.length} file caricato/i`);
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore upload");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (fid) => {
    if (!window.confirm("Eliminare questo DDT?")) return;
    try {
      await axios.delete(`${API}/admin/warehouse/movements/${movement.id}/ddt/${fid}`, authHeaders());
      toast.success("File eliminato");
      onUpdated();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="mov-ddt-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-[#09090B]">DDT del Movimento</h3>
            <p className="text-xs text-zinc-500">{movement.movement_type === "ingresso" ? "INGRESSO" : "USCITA"} · {movement.client_name || movement.company || movement.client_email}</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700" data-testid="mov-ddt-close"><X size={20} /></button>
        </div>

        <div className="p-5 flex-1 overflow-auto space-y-4">
          <label className="block border-2 border-dashed border-zinc-300 rounded-sm p-6 text-center cursor-pointer hover:bg-zinc-50" data-testid="mov-ddt-drop">
            <Upload size={22} className="mx-auto text-zinc-400 mb-2" />
            <p className="text-sm font-bold text-[#09090B]">Carica DDT</p>
            <p className="text-xs text-zinc-500">PDF, JPG, PNG o WEBP · max 8 MB per file · selezione multipla ok</p>
            <input type="file" multiple accept={ACCEPTED} className="hidden"
              onChange={(e) => upload(e.target.files)}
              disabled={uploading}
              data-testid="mov-ddt-input"
            />
          </label>

          {files.length === 0 ? (
            <p className="text-sm text-zinc-400 text-center py-4">Nessun DDT allegato</p>
          ) : (
            <ul className="divide-y divide-zinc-100 border border-zinc-200 rounded-sm">
              {files.map((f) => (
                <li key={f.id} className="flex items-center gap-3 px-3 py-2" data-testid={`mov-ddt-file-${f.id}`}>
                  {f.content_type?.startsWith("image/")
                    ? <ImageIcon size={16} className="text-purple-600" />
                    : <FileText size={16} className="text-red-600" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{f.filename}</p>
                    <p className="text-[10px] text-zinc-400">{f.content_type} · {f.uploaded_at?.slice(0,10)}</p>
                  </div>
                  <a
                    href={`data:${f.content_type};base64,${f.data_base64}`}
                    download={f.filename}
                    className="text-xs font-bold uppercase tracking-wide text-blue-700 hover:underline"
                  >Scarica</a>
                  <button onClick={() => remove(f.id)} className="text-red-500 hover:text-red-700" title="Elimina">
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MovementsTable({ items, onDelete, onEdit, onRefresh }) {
  const [expanded, setExpanded] = useState(null);
  const [ddtMov, setDdtMov] = useState(null);

  const printLabels = (m) => {
    // `base` = origine pubblica del pannello: finisce nel QR dell'etichetta
    const url = `${API}/admin/warehouse/movements/${m.id}/labels?base=${encodeURIComponent(window.location.origin)}`;
    // Fetch as blob to bypass auth via cookie
    axios.get(url, { ...authHeaders(), responseType: "blob" }).then((res) => {
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(blobUrl, "_blank");
    }).catch(() => toast.error("Errore generazione etichette"));
  };

  if (!items.length) return <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center text-zinc-400">Nessun movimento registrato</div>;

  return (
    <div className="space-y-2">
      {items.map((m, i) => (
        <div key={m.id || i} className="bg-white border border-zinc-200 rounded-sm" data-testid={`mov-row-${i}`}>
          <div className="flex flex-wrap items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpanded(expanded === i ? null : i)}>
            <div className={`w-8 h-8 shrink-0 rounded-sm flex items-center justify-center ${m.movement_type === "ingresso" ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600"}`}>
              {m.movement_type === "ingresso" ? <ArrowDownToLine size={16} /> : <ArrowUpFromLine size={16} />}
            </div>
            <div className="flex-1 min-w-[180px]">
              <p className="font-semibold text-sm">{m.movement_type === "ingresso" ? "INGRESSO" : "USCITA"} — {m.company || m.client_name}</p>
              <p className="text-xs text-zinc-400">{m.description || m.reference || "-"} | Reg. {m.created_at?.slice(0,10)}</p>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                <span className="text-emerald-700 font-semibold">Ingresso: {itDate(m.entry_date || m.created_at?.slice(0,10))}</span>
                <span className="mx-1 text-zinc-300">·</span>
                <span className={m.exit_date ? "text-red-600 font-semibold" : "text-zinc-400"}>Uscita: {m.exit_date ? itDate(m.exit_date) : "in giacenza"}</span>
              </p>
            </div>
            <span
              title={m.exit_date ? "Giacenza chiusa (uscita - ingresso)" : "Giacenza in corso (oggi - ingresso)"}
              className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wide rounded-sm border ${m.exit_date ? "border-zinc-200 bg-zinc-50 text-zinc-600" : "border-amber-300 bg-amber-50 text-amber-700"}`}
              data-testid={`mov-days-${i}`}
            >
              {m.storage_days === null || m.storage_days === undefined ? "- gg" : `${m.storage_days} gg`}
            </span>
            <span className="text-sm font-bold">{m.items?.length || 0} art.</span>
            {m.cost > 0 && <span className="text-sm font-bold text-[#FF5200]">{m.cost.toFixed(2)} EUR</span>}
            <button
              onClick={(e) => { e.stopPropagation(); setDdtMov(m); }}
              title={`DDT allegati: ${(m.ddt_files || []).length}`}
              className={`flex items-center gap-1 px-2 py-1 border text-[10px] font-bold uppercase rounded-sm hover:bg-zinc-50 ${(m.ddt_files || []).length > 0 ? "border-blue-300 text-blue-700 bg-blue-50" : "border-zinc-200 text-zinc-600"}`}
              data-testid={`mov-ddt-btn-${i}`}
            >
              <Paperclip size={12} /> DDT{(m.ddt_files || []).length > 0 ? ` (${m.ddt_files.length})` : ""}
            </button>
            {m.movement_type === "ingresso" && (m.items || []).length > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); printLabels(m); }}
                title="Stampa etichette colli (4 per foglio A4)"
                className="flex items-center gap-1 px-2 py-1 border border-purple-300 bg-purple-50 text-purple-700 text-[10px] font-bold uppercase rounded-sm hover:bg-purple-100"
                data-testid={`mov-labels-btn-${i}`}
              >
                <Printer size={12} /> Etichette
              </button>
            )}
            <button onClick={(e) => { e.stopPropagation(); onEdit(m); }} data-testid={`mov-edit-${i}`}
              className="flex items-center gap-1 px-2 py-1 border border-zinc-200 text-[10px] font-bold uppercase rounded-sm hover:bg-zinc-50 text-zinc-600">
              <PenLine size={12} /> Modifica
            </button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(m.id); }} className="p-1 text-zinc-300 hover:text-red-500"><Trash2 size={16} /></button>
            {expanded === i ? <ChevronUp size={16} className="text-zinc-400" /> : <ChevronDown size={16} className="text-zinc-400" />}
          </div>
          {expanded === i && (
            <div className="px-4 pb-3 border-t border-zinc-100">
              <table className="w-full text-xs mt-2">
                <thead><tr className="text-zinc-400 uppercase"><th className="text-left py-1">Articolo</th><th className="text-right py-1">Qty</th><th className="text-left py-1">Unita</th></tr></thead>
                <tbody>{(m.items || []).map((it, j) => (
                  <tr key={j} className="border-t border-zinc-50"><td className="py-1">{it.name}</td><td className="text-right py-1 font-bold">{it.quantity}</td><td className="py-1 text-zinc-500">{it.unit || "pz"}</td></tr>
                ))}</tbody>
              </table>
              {m.note && <p className="text-xs text-zinc-500 mt-2">Note: {m.note}</p>}
              {m.location_code && <p className="text-xs text-zinc-400 mt-1">Posizione: {m.location_code}</p>}
            </div>
          )}
        </div>
      ))}
      {ddtMov && (
        <DdtModal movement={ddtMov} onClose={() => setDdtMov(null)} onUpdated={() => { onRefresh?.(); setDdtMov(null); }} />
      )}
    </div>
  );
}
